<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0 text-capitalize">Digitain Report</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item">
                <router-link to="/">Home</router-link>
              </li>
              <li class="breadcrumb-item">Advanced Reports</li>
              <li class="breadcrumb-item active text-capitalize">
                <i class="fas fa-chart-bar"></i> Digitain
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <data-table-wrapper
            ref="dataTable"
            :columns="columns"
            :filters="filters"
            :load-data-callback="loadDataCallback"
            disable-primary-column="true"
            disable-edit-action="true"
            disable-delete-action="true"
            :disable-auto-load="true"
            :endpoint="'/game-actions/reports/advanced/digitain'"
            api-service="analytics"
        >
          <template v-slot:filter-buttons>
            <button class="btn btn-default float-right" @click="doExport">
              <span v-if="!exportInProgress">
                  <i class="fa fa-cloud-download"></i> {{ $t('Export') }}
              </span>
              <span v-else>
                  <i class="fas fa-spinner fa-pulse"></i> {{ $t('Export') }}
              </span>
            </button>
          </template>
        </data-table-wrapper>
      </div>
    </section>
  </div>
</template>

<script>
import * as formatUtils from '../helpers/formatUtils';
import DataTableWrapper from '../components/DataTableWrapper';
import {mapGetters} from "vuex";
import * as dateUtils from "@/helpers/dateUtils";

export default {
  components: {
    DataTableWrapper
  },
  data() {
    return {
      exportInProgress: false,
      loadDataCallback: (component, respData) => {
        component.items = respData;
        component.pagination.onPage = component.pagination.total = component.items.length;
        component.pagination.page = 1;
        component.showEmpty = true;
      },
      filters: [
        {
          field: 'fromDate',
          placeholder: 'From Date',
          label: 'From Date',
          type: 'date-picker',
          shortcuts: dateUtils.getFilterShortcuts()
        },
        {
          field: 'toDate',
          placeholder: 'To Date',
          label: 'To Date',
          type: 'date-picker',
          shortcuts: dateUtils.getFilterShortcuts()
        },
        {
          field: 'partnerIds',
          placeholder: 'Select partners',
          label: 'Partners',
          type: 'multiselect',
          optionLabel: 'name',
          multiple: true,
          getValue: (partners) => { return partners ? partners.map(partner => partner.id) :[]},
          getOptions: () => this.getPartners,
          trackBy: 'id'
        },
        {
          field: 'operatorIds',
          placeholder: 'Select operators',
          label: 'Operators',
          type: 'multiselect',
          optionLabel: 'name',
          multiple: true,
          getValue: (operators) => { return operators ? operators.map(operator => operator.id) : [] },
          getOptions: () => this.getOperatorsOptions,
          trackBy: 'id'
        },
        {
          field: 'gameIds',
          placeholder: 'Select games',
          label: 'Games',
          type: 'multiselect',
          optionLabel: 'name',
          multiple: true,
          getValue: (games) => { return games ? games.map(game => game.id) : [] },
          getOptions: () => this.getGames,
          trackBy: 'id'
        },
      ],
    };
  },
  computed: {
    ...mapGetters([
      'getGames',
      'getGame',
      'getPartners',
      'getPartner',
      'getOperators',
      'getOperator',
      'getCurrency',
      'isOperator',
    ]),
    columns() {
      return [
        {
          field: 'gameId',
          title: 'Game',
          sorting: false,
          callback: ({row}) => {
            const game = this.getGame(row.gameId?.toString());
            return game ? game.name : row.gameId;
          },
        },
        {
          field: 'partnerId',
          title: 'Partner',
          sorting: false,
          callback: ({row}) => {
            const partner = this.getPartner(row.partnerId?.toString());
            return partner ? partner.name : row.partnerId;
          },
        },
        {
          field: 'operatorId',
          title: 'Operator',
          sorting: false,
          callback: ({row}) => {
            const operator = this.getOperator(row.operatorId?.toString());
            return operator ? operator.name : row.operatorId;
          },
        },
        {
          field: 'currency',
          title: 'Currency',
          sorting: false,
        },
        {
          field: 'bets',
          title: 'Bets',
          sorting: false,
          callback: ({row}) => this.formatNumber(row.bets),
        },
        {
          field: 'wins',
          title: 'Wins',
          sorting: false,
          callback: ({row}) => this.formatNumber(row.wins),
        },
        {
          field: 'ggr',
          title: 'GGR',
          sorting: false,
          callback: ({row}) => this.formatNumber(row.bets - row.wins),
        },
        {
          field: 'rateEur',
          title: 'Rate EUR',
          sorting: false,
          callback: ({row}) => this.formatRateValue(row.currency),
        },
        {
          field: 'betsEur',
          title: 'Bets EUR',
          sorting: false,
          callback: ({row}) => this.formatEurValue(row.bets, row.currency),
        },
        {
          field: 'winsEur',
          title: 'Wins EUR',
          sorting: false,
          callback: ({row}) => this.formatEurValue(row.wins, row.currency),
        },
        {
          field: 'ggrEur',
          title: 'GGR EUR',
          sorting: false,
          callback: ({row}) => this.formatEurValue(row.bets - row.wins, row.currency),
        },
        {
          field: 'betsCount',
          title: 'Bets Count',
          sorting: false,
        },
      ];
    },
    getOperatorsOptions() {
      return this.getOperators.filter((operator) => {
        let selectedPartnerIds = this.$refs.dataTable.getFilterValue('partnerIds');
        if (selectedPartnerIds.length === 0) {
          return true;
        }

        if (!operator.partnerId) {
          return false;
        }

        return selectedPartnerIds.includes(operator.partnerId);
      });
    },
  },
  methods: {
    formatNumber(value) {
      return formatUtils.formatNumberToFixed(value);
    },
    getRate(currency) {
      try {
        return this.getCurrency(currency).rate;
      } catch (e) {
        return undefined;
      }
    },
    formatRateValue(currency) {
      const rate = this.getRate(currency);
      return rate !== undefined ? this.formatNumber(rate) : 'n/a';
    },
    formatEurValue(amount, currency) {
      const rate = this.getRate(currency);
      return rate !== undefined ? this.formatNumber(amount * rate) : 'n/a';
    },
    doExport() {
      if (this.exportInProgress) {
        return;
      }
      this.exportInProgress = true;
      this.$exportService.doExport('game-actions/reports/advanced/digitain', this.$refs.dataTable.getPageQuery()).finally(() => {
        this.exportInProgress = false;
      });
    },
  },
}
</script>